import { useParams, Link } from "react-router-dom";
import { useBrandMap, useProducts } from "../api/products";
import { useState, useEffect, useMemo } from "react";
import { Button, Container, BackChevron } from "../Components/ui";

const HIDDEN_VALUES = [
  "メーカー記載なし",
  "記載なし",
  "未記載",
  "不明",
  "なし",
  "-",
  "",
  null,
  undefined,
];

const DETAIL_LABELS = {
  mountType: "設置タイプ",
  connectivity: "接続方式",
  recordingMethod: "録画方式",
  lensType: "レンズ情報",
  waterproofDustproof: "防水・防塵性能",
  resolution: "解像度",
  lowLightPerformance: "低照度性能",
  aiFeatures: "AI機能",
};

function isVisibleValue(value) {
  if (Array.isArray(value)) return value.length > 0;
  return !HIDDEN_VALUES.includes(value);
}

const PAGE_SIZE = 20;

const show = (v) => (Array.isArray(v) ? v.join("、") : v);

export default function BrandProducts() {
  const { brandSlug } = useParams();
  // メーカーと商品は、API（データベース）から読み込む
  const { brandMap, ready: brandMapReady } = useBrandMap();
  const { products, ready: productsReady } = useProducts();
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const loading = !brandMapReady || !productsReady;
  const makerName = brandMap[brandSlug];

  useEffect(() => {
    if (loading) return;
    document.title = makerName
      ? `${makerName}の防犯カメラ一覧｜CAMARAGO`
      : "メーカーが見つかりません｜CAMARAGO";
  }, [makerName, loading]);

  const brandProducts = useMemo(
    () => products.filter((product) => product.makerName === makerName),
    [products, makerName]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return brandProducts;
    return brandProducts.filter((p) =>
      `${p.productName ?? ""} ${p.modelNumber ?? ""}`.toLowerCase().includes(q)
    );
  }, [brandProducts, query]);

  if (loading) {
    return (
      <div className="bg-fill-2 py-24 text-center text-[15px] text-ink-2" role="status">
        読み込み中です…
      </div>
    );
  }

  if (!makerName) {
    return (
      <div className="bg-fill-2 py-20">
        <Container narrow className="text-center">
          <h1 className="text-[28px] font-bold">メーカーが見つかりません</h1>
          <p className="mt-3 text-ink-2">指定されたメーカー情報は現在登録されていません。</p>
          <Button to="/brands" variant="secondary" className="mt-8">
            メーカー一覧へ
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-fill-2 pb-20">
      <Container className="pt-6 md:pt-10">
        <Link to="/brands" className="-ml-1 inline-flex min-h-11 items-center gap-1 text-[15px] text-tint hover:text-tint-dark">
          <BackChevron className="h-4 w-3" />
          メーカー一覧
        </Link>

        <div className="mt-4 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-[32px] font-bold leading-tight tracking-tight md:text-[44px]">{makerName}</h1>
            <p className="mt-2 text-[15px] text-ink-2 md:text-[17px]">
              {brandProducts.length}機種を掲載しています。メーカーが公表している仕様をまとめています。
            </p>
          </div>
          <Link to="/wizard" className="shrink-0 rounded-[14px] bg-white px-5 py-4 transition-colors hover:bg-white/70">
            <span className="block text-[13px] text-ink-2">自分の条件に合うか知りたいときは</span>
            <span className="mt-0.5 block text-[17px] font-semibold text-tint">診断で絞り込む ›</span>
          </Link>
        </div>

        <div className="mt-8">
          <label className="relative block md:max-w-sm">
            <span className="sr-only">商品名・型番で探す</span>
            <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-2" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <circle cx="7" cy="7" r="5" />
              <path d="M11 11l3.5 3.5" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setLimit(PAGE_SIZE);
              }}
              placeholder="商品名・型番で探す"
              className="h-11 w-full rounded-[10px] bg-[#e3e3e8] pl-9 pr-3 text-[17px] text-ink placeholder:text-ink-2 focus:bg-white focus:outline-none focus:ring-2 focus:ring-tint/40"
            />
          </label>
          {query && (
            <p className="mt-2 px-1 text-[13px] text-ink-2" aria-live="polite">
              {visible.length}件見つかりました
            </p>
          )}
        </div>

        <section className="mt-6 grid gap-4 lg:grid-cols-2">
          {visible.slice(0, limit).map((product) => (
            <ProductCard key={product.modelNumber || product.productName} product={product} />
          ))}
        </section>

        {visible.length > limit && (
          <div className="mt-6 flex flex-col items-center gap-2">
            <Button variant="secondary" className="bg-white" onClick={() => setLimit((n) => n + PAGE_SIZE)}>
              さらに表示する
            </Button>
            <span className="text-[13px] text-ink-2">
              {visible.length}機種中 {limit}機種を表示中
            </span>
          </div>
        )}

        <p className="mt-10 px-1 text-[13px] text-ink-2">
          ※ 仕様は各メーカーの公表資料をもとに掲載しています。最新の情報は各メーカーのサイトでご確認ください。
        </p>
      </Container>
    </div>
  );
}

function ProductCard({ product }) {
  const features = product.features ?? {};
  const rows = Object.entries(DETAIL_LABELS).filter(([key]) => isVisibleValue(features[key]));

  return (
    <article className="flex flex-col gap-4 rounded-[18px] bg-white p-5">
      <div>
        <div className="text-[13px] text-ink-2">{product.modelNumber || "型番未登録"}</div>
        <h2 className="mt-0.5 text-[18px] font-bold leading-snug">{product.productName || "商品名未登録"}</h2>
      </div>

      <dl className="rounded-xl bg-fill px-3.5 text-[14px]">
        {rows.length === 0 && <div className="py-3 text-ink-2">表示できる情報がありません</div>}
        {rows.map(([key, label], i) => (
          <div key={key} className={`py-2.5 ${i < rows.length - 1 ? "border-b border-line" : ""}`}>
            <dt className="text-ink-2">{label}</dt>
            <dd className="mt-0.5 break-words leading-relaxed">{show(features[key])}</dd>
          </div>
        ))}
      </dl>
    </article>
  );
}
